import { useRef, useMemo, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { Html, Text, Sphere } from "@react-three/drei";
import type { SensorData } from "@/contexts/data-context";
import * as THREE from "three";
import { Badge } from "@/components/ui/badge";
import {
  Thermometer,
  Droplets,
  Gauge,
  Zap,
  Sun,
  Wind,
  Activity,
  Users,
} from "lucide-react";
import { useData } from "@/contexts/data-context";
import { useScene } from "@/contexts/scene-context";

interface SensorVisualizationProps {
  sensor: SensorData;
}

export default function SensorVisualization({
  sensor,
}: SensorVisualizationProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const glowRef = useRef<THREE.Mesh>(null);
  const groupRef = useRef<THREE.Group>(null);
  const [hovered, setHovered] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { updateSensorValue } = useData();
  const { sceneState } = useScene();

  // Determine sensor model based on type
  const sensorModel = useMemo(() => {
    return getSensorModel(sensor.type);
  }, [sensor.type]);

  // Animate based on sensor value and status - optimize for performance
  useFrame((state) => {
    if (meshRef.current) {
      const normalizedValue =
        (sensor.value - sensor.min) / (sensor.max - sensor.min);

      // Scale based on value
      meshRef.current.scale.y = 0.5 + normalizedValue * 1.5;

      // Only do animation effects in medium quality or higher
      if (sceneState.renderQuality !== "low") {
        // Pulse effect for critical status
        if (sensor.status === "critical") {
          const pulse = Math.sin(state.clock.elapsedTime * 4) * 0.1 + 1;
          meshRef.current.scale.multiplyScalar(pulse);
        }

        // Subtle pulse for warning status
        if (sensor.status === "warning") {
          const pulse = Math.sin(state.clock.elapsedTime * 2) * 0.05 + 1;
          meshRef.current.scale.multiplyScalar(pulse);
        }

        // Offline effect
        if (!sensor.isOnline) {
          meshRef.current.visible = Math.sin(state.clock.elapsedTime * 2) > 0;
        }
      }
    }

    // Animate glow effect - only in medium quality or higher
    if (glowRef.current && sceneState.renderQuality !== "low") {
      if (hovered || showDetails) {
        glowRef.current.scale.set(1.2, 1.2, 1.2);
      } else {
        glowRef.current.scale.set(1, 1, 1);
      }

      // Pulse glow for critical status
      if (sensor.status === "critical") {
        const pulse = Math.sin(state.clock.elapsedTime * 3) * 0.3 + 1.2;
        glowRef.current.scale.set(pulse, pulse, pulse);
      }

      // Pulse glow for warning status
      if (sensor.status === "warning") {
        const pulse = Math.sin(state.clock.elapsedTime * 2) * 0.2 + 1.1;
        glowRef.current.scale.set(pulse, pulse, pulse);
      }
    }
  });

  // Determine if sensor should be visible based on type
  const isVisible = useMemo(() => {
    // Show all sensors when hovered or details shown
    if (hovered || showDetails) return true;

    // Hide certain sensor types that might clutter the view
    const hiddenTypes: string[] = [];
    return !hiddenTypes.includes(sensor.type);
  }, [sensor.type, hovered, showDetails]);

  if (!isVisible) return null;

  const getColor = () => {
    if (!sensor.isOnline) return "#6b7280"; // Gray for offline

    switch (sensor.status) {
      case "critical":
        return "#ef4444";
      case "warning":
        return "#f59e0b";
      default:
        return "#10b981";
    }
  };

  const getIcon = () => {
    switch (sensor.type) {
      case "temperature":
        return <Thermometer className="w-4 h-4" />;
      case "humidity":
      case "water":
        return <Droplets className="w-4 h-4" />;
      case "pressure":
        return <Gauge className="w-4 h-4" />;
      case "light":
        return <Sun className="w-4 h-4" />;
      case "electrical":
      case "power":
      case "voltage":
      case "current":
        return <Zap className="w-4 h-4" />;
      case "co2":
      case "pm25":
      case "pm10":
      case "voc":
        return <Wind className="w-4 h-4" />;
      case "noise":
      case "motion":
        return <Activity className="w-4 h-4" />;
      case "occupancy":
        return <Users className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getStatusColor = () => {
    if (!sensor.isOnline) return "bg-muted text-muted-foreground";

    switch (sensor.status) {
      case "critical":
        return "bg-destructive text-destructive-foreground";
      case "warning":
        return "bg-warning text-warning-foreground";
      default:
        return "bg-success text-success-foreground";
    }
  };

  // Handle click to toggle details
  const handleClick = (event: any) => {
    event.stopPropagation();
    console.log(`Clicked on sensor: ${sensor.name} (${sensor.type})`);
    setShowDetails(!showDetails);
  };

  const handlePointerOver = (event: any) => {
    event.stopPropagation();
    setHovered(true);
    document.body.style.cursor = "pointer";
  };

  const handlePointerOut = (event: any) => {
    event.stopPropagation();
    setHovered(false);
    document.body.style.cursor = "default";
  };

  return (
    <group ref={groupRef} position={sensor.position}>
      {/* Invisible clickable area for better interaction - this must be the main interactive element */}
      <mesh
        position={[0, 0.5, 0]}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onClick={handleClick}
      >
        <sphereGeometry args={[0.8, 16, 16]} />
        <meshBasicMaterial transparent opacity={0} visible={false} />
      </mesh>

      {/* Sensor indicator */}
      <mesh
        ref={meshRef}
        position={[0, 0.5, 0]}
        castShadow={sceneState.enableShadows}
      >
        <cylinderGeometry args={[0.1, 0.1, 1, 8]} />
        <meshStandardMaterial
          color={getColor()}
          emissive={getColor()}
          emissiveIntensity={0.5}
          roughness={0.4}
          metalness={0.6}
          transparent
          opacity={0.8}
        />
      </mesh>

      {/* Glow effect - only in medium quality or higher */}
      {sceneState.renderQuality !== "low" && (
        <Sphere ref={glowRef} args={[0.15, 16, 16]} position={[0, 0.5, 0]}>
          <meshBasicMaterial
            color={getColor()}
            transparent
            opacity={0.2}
            depthWrite={false}
          />
        </Sphere>
      )}

      {/* Base */}
      <mesh
        position={[0, 0, 0]}
        castShadow={sceneState.enableShadows}
        receiveShadow={sceneState.enableShadows}
      >
        <cylinderGeometry args={[0.2, 0.2, 0.1, 8]} />
        <meshStandardMaterial color="#374151" roughness={0.6} metalness={0.4} />
      </mesh>

      {/* Sensor type label */}
      <Text
        position={[0, 0.05, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        fontSize={0.08}
        color="#9ca3af"
        anchorX="center"
        anchorY="middle"
      >
        {sensor.type.toUpperCase()}
      </Text>

      {/* Data display */}
      <Html
        position={[0, 1.5, 0]}
        center
        distanceFactor={8}
        occlude
        className={`pointer-events-none select-none transition-opacity duration-300 ${
          hovered || showDetails ? "opacity-100" : "opacity-80"
        }`}
        transform
      >
        <div className="bg-background/90 backdrop-blur-sm border border-border rounded-lg p-2 shadow-lg min-w-32 transition-all duration-300 ease-in-out transform hover:scale-105">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1 text-sm font-medium">
              {getIcon()}
              <span>{sensor.name}</span>
            </div>
            <Badge variant="outline" className={`text-xs ${getStatusColor()}`}>
              {sensor.isOnline ? sensor.status : "offline"}
            </Badge>
          </div>
          <div className="text-lg font-bold text-center">
            {sensor.value.toFixed(1)} {sensor.unit}
          </div>

          {/* Extended details when clicked */}
          {showDetails && (
            <div className="mt-2 pt-2 border-t border-border">
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                <span className="text-muted-foreground">Range:</span>
                <span>
                  {sensor.min} - {sensor.max} {sensor.unit}
                </span>

                <span className="text-muted-foreground">Updated:</span>
                <span>{sensor.lastUpdated.toLocaleTimeString()}</span>

                <span className="text-muted-foreground">Accuracy:</span>
                <span>{sensor.accuracy.toFixed(1)}%</span>

                {sensor.batteryLevel !== undefined && (
                  <>
                    <span className="text-muted-foreground">Battery:</span>
                    <span
                      className={
                        sensor.batteryLevel < 20 ? "text-destructive" : ""
                      }
                    >
                      {sensor.batteryLevel.toFixed(0)}%
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </Html>
    </group>
  );
}

// Helper function to get sensor model based on type
function getSensorModel(type: string) {
  switch (type) {
    case "temperature":
      return "temperature_sensor";
    case "humidity":
      return "humidity_sensor";
    case "pressure":
      return "pressure_sensor";
    case "light":
      return "light_sensor";
    case "electrical":
    case "power":
    case "voltage":
    case "current":
      return "electrical_sensor";
    case "co2":
    case "pm25":
    case "pm10":
    case "voc":
      return "air_quality_sensor";
    case "noise":
      return "noise_sensor";
    case "motion":
      return "motion_sensor";
    case "occupancy":
      return "occupancy_sensor";
    case "water":
      return "water_sensor";
    default:
      return "generic_sensor";
  }
}
